import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { secsData } from '../utils/secsDataLoader';

export default function Sidebar() {
  const navigate = useNavigate();
  const { sidebarCollapsed, selectedStream, setSelectedStream, sidebarWidth, setSidebarWidth } = useAppStore();
  const [expandedStreams, setExpandedStreams] = useState<Set<string>>(new Set(['S1']));
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const toggleStream = (streamId: string) => {
    const newExpanded = new Set(expandedStreams);
    if (newExpanded.has(streamId)) {
      newExpanded.delete(streamId);
    } else {
      newExpanded.add(streamId);
    }
    setExpandedStreams(newExpanded);
  };

  const handleMessageClick = (streamId: string, funcId: string) => {
    setSelectedStream(streamId);
    navigate(`/message/${streamId}${funcId}`);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleDoubleClick = () => {
    setSidebarWidth(256); // Reset to default width
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, setSidebarWidth]);

  if (sidebarCollapsed) {
    return null;
  }

  return (
    <aside
      ref={sidebarRef}
      style={{ width: `${sidebarWidth}px` }}
      className="fixed left-0 top-[65px] h-[calc(100vh-65px)] bg-[var(--color-bg-secondary)] border-r border-[var(--color-border)] overflow-y-auto scrollbar-thin group"
    >
      <div className="p-4">
        <h2 className="text-sm font-semibold text-[var(--color-text-tertiary)] uppercase tracking-wider mb-3">
          Streams
        </h2>

        <nav className="space-y-1">
          {Object.entries(secsData.streams).map(([streamId, stream]) => {
            const isExpanded = expandedStreams.has(streamId);
            const isSelected = selectedStream === streamId;

            return (
              <div key={streamId}>
                <button
                  onClick={() => toggleStream(streamId)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isSelected
                      ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100'
                      : 'hover:bg-[var(--color-bg-tertiary)] text-[var(--color-text-primary)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold">S{stream.number}</span>
                    <span className="truncate">{stream.name}</span>
                  </div>
                  <svg
                    className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {isExpanded && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-[var(--color-border)] pl-2">
                    {Object.entries(stream.functions).map(([funcId, func]) => {
                      const isPrimary = func.messageType === 'primary';

                      return (
                        <button
                          key={funcId}
                          onClick={() => handleMessageClick(streamId, funcId)}
                          className="w-full text-left px-3 py-1.5 rounded text-xs hover:bg-[var(--color-bg-tertiary)] transition-colors group"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className={`font-mono font-semibold ${
                                isPrimary
                                  ? 'text-orange-600 dark:text-orange-400'
                                  : 'text-teal-600 dark:text-teal-400'
                              }`}
                            >
                              {funcId}
                            </span>
                            <span className="text-[var(--color-text-secondary)] group-hover:text-[var(--color-text-primary)] truncate">
                              {func.name}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      <div className="p-4 border-t border-[var(--color-border)] bg-[var(--color-bg-primary)]">
        <div className="text-xs text-[var(--color-text-tertiary)]">
          <div className="flex items-center justify-between mb-2">
            <span>Version:</span>
            <span className="font-mono">{secsData.version}</span>
          </div>
          <div className="flex items-center justify-between">
            <span>Updated:</span>
            <span className="font-mono">{secsData.lastUpdated}</span>
          </div>
        </div>
      </div>

      {/* Resize Handle */}
      <div
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:w-1.5 hover:bg-primary-500 transition-all ${
          isResizing ? 'w-1.5 bg-primary-500' : 'bg-transparent group-hover:bg-[var(--color-border)]'
        }`}
        title="Drag to resize / double-click to reset"
      />
    </aside>
  );
}
